import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { GraduationCap, Layers3, Globe2, ArrowUpRight } from "lucide-react";

const ITEMS = [
  {
    icon: GraduationCap,
    num: "01",
    eyebrow: "Niveaux d'études",
    title: "Post-bac à Bac+5",
    desc: "Admission sur entretien et concours. Accessible post-bac et en admission parallèle de bac+2 à bac+5.",
    to: "/formations#niveaux",
  },
  {
    icon: Layers3,
    num: "02",
    eyebrow: "Flexibilité",
    title: "3 rythmes",
    desc: "Suivez votre formation avec le rythme qui vous correspond : initial, alternance ou e-learning.",
    to: "/formations#rythmes",
  },
  {
    icon: Globe2,
    num: "03",
    eyebrow: "Réseau mondial",
    title: "7 campus",
    desc: "Ouvrez-vous au monde en partant étudier chaque année sur un de nos campus, quel que soit votre rythme.",
    to: "/formations#campus-monde",
  },
];

const Card = ({ item, index }) => {
  const [hovered, setHovered] = useState(false);
  const Icon = item.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
      onHoverStart={() => setHovered(true)}
      onHoverEnd={() => setHovered(false)}
      whileHover={{ y: -8 }}
      data-testid={`bandeau-item-${index}`}
      className="relative group bg-white border border-navy-100 overflow-hidden cursor-default isolate rounded-2xl"
      style={{
        boxShadow: hovered
          ? "0 30px 70px -20px rgba(0, 35, 98, 0.30), 0 0 0 1px rgba(237, 41, 57, 0.10)"
          : "0 8px 28px -16px rgba(0, 35, 98, 0.12)",
      }}
    >
      {/* Animated background gradient (slides up on hover) */}
      <div
        className={`absolute inset-0 bg-gradient-to-br from-navy-500 via-navy-700 to-[#000e27] origin-bottom transition-transform duration-[700ms] ease-[cubic-bezier(0.65,0,0.35,1)] -z-10 ${
          hovered ? "scale-y-100" : "scale-y-0"
        }`}
      />

      {/* Background pattern dots — visible only on hover */}
      <svg
        className={`absolute inset-0 w-full h-full pointer-events-none transition-opacity duration-700 ${
          hovered ? "opacity-[0.08]" : "opacity-0"
        }`}
        preserveAspectRatio="none"
      >
        <defs>
          <pattern id={`trio-pat-${index}`} width="16" height="16" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1" fill="white" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#trio-pat-${index})`} />
      </svg>

      {/* Glow orb behind icon (only on hover) */}
      <div
        className={`absolute -top-16 -right-16 w-44 h-44 rounded-full blur-3xl transition-opacity duration-700 ${
          hovered ? "opacity-100 bg-red-500/30" : "opacity-0"
        }`}
      />

      {/* GIANT background number */}
      <span
        className={`absolute -top-2 right-4 font-display text-[170px] md:text-[200px] leading-none select-none pointer-events-none transition-all duration-500 ${
          hovered
            ? "text-white/[0.08] scale-110"
            : "text-navy-700/[0.06] scale-100"
        }`}
        aria-hidden
      >
        {item.num}
      </span>

      <div className="relative p-8 md:p-10 lg:p-12 flex flex-col gap-6 min-h-[380px]">
        {/* Tilted icon square — rotates on hover */}
        <motion.div
          animate={{ rotate: hovered ? -8 : -4, scale: hovered ? 1.05 : 1 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-16 h-16"
        >
          <div
            className={`absolute inset-0 transition-colors duration-500 ${
              hovered ? "bg-red-500" : "bg-navy-50"
            }`}
            style={{ borderRadius: "8px" }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <Icon
              className={`h-7 w-7 transition-colors duration-500 ${
                hovered ? "text-white" : "text-navy-500"
              }`}
              strokeWidth={1.5}
            />
          </div>
        </motion.div>

        {/* Eyebrow + Title + Description */}
        <div className="flex-1">
          <div
            className={`text-[10px] uppercase tracking-[0.22em] font-semibold mb-2 transition-colors duration-500 ${
              hovered ? "text-red-400" : "text-red-500"
            }`}
          >
            {item.eyebrow}
          </div>
          <h3
            className={`font-display text-3xl md:text-4xl leading-[1.05] mb-4 transition-colors duration-500 ${
              hovered ? "text-white" : "text-navy-700"
            }`}
          >
            {item.title}
          </h3>
          <p
            className={`text-[14px] leading-relaxed transition-colors duration-500 ${
              hovered ? "text-white/80" : "text-[#555]"
            }`}
          >
            {item.desc}
          </p>
        </div>

        {/* CTA with animated arrow + underline */}
        <Link
          to={item.to}
          data-testid={`bandeau-link-${index}`}
          className={`relative inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] font-semibold pb-1.5 self-start transition-colors duration-500 ${
            hovered ? "text-white" : "text-navy-700"
          }`}
        >
          <span>Explorer</span>
          <motion.span
            animate={{ x: hovered ? 4 : 0, y: hovered ? -4 : 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="inline-flex"
          >
            <ArrowUpRight className="h-4 w-4" />
          </motion.span>
          {/* Animated underline */}
          <span
            className={`absolute left-0 right-0 bottom-0 h-[1px] origin-left transition-transform duration-500 ${
              hovered ? "bg-white scale-x-100" : "bg-navy-200 scale-x-100"
            }`}
          />
        </Link>
      </div>

      {/* Tricolor stripe at the bottom (slides in on hover) */}
      <div className="absolute bottom-0 left-0 right-0 h-[3px] flex overflow-hidden">
        <motion.span
          initial={false}
          animate={{ scaleX: hovered ? 1 : 0 }}
          transition={{ duration: 0.5, ease: [0.65, 0, 0.35, 1], delay: hovered ? 0 : 0 }}
          className="flex-1 bg-[#002362] origin-left"
        />
        <motion.span
          initial={false}
          animate={{ scaleX: hovered ? 1 : 0 }}
          transition={{ duration: 0.5, ease: [0.65, 0, 0.35, 1], delay: hovered ? 0.1 : 0 }}
          className="flex-1 bg-white origin-left"
        />
        <motion.span
          initial={false}
          animate={{ scaleX: hovered ? 1 : 0 }}
          transition={{ duration: 0.5, ease: [0.65, 0, 0.35, 1], delay: hovered ? 0.2 : 0 }}
          className="flex-1 bg-[#ED2939] origin-left"
        />
      </div>
    </motion.div>
  );
};

export const BandeauTrio = () => {
  return (
    <section
      data-testid="bandeau-trio"
      className="relative bg-[#FAF8F3] overflow-hidden py-16 md:py-24"
    >
      {/* Decorative background blurs */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 left-1/4 w-[28rem] h-[28rem] bg-red-500/5 blur-3xl rounded-full" />
        <div className="absolute -bottom-32 right-1/4 w-[28rem] h-[28rem] bg-navy-500/5 blur-3xl rounded-full" />
      </div>

      <div className="container-edge relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {ITEMS.map((item, i) => (
            <Card key={item.title} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default BandeauTrio;